import { PaymentProcessor } from "@/types/auth";
import type {
  ClinicSetupFlowType,
  ClinicSetupStep,
  StepStatus,
} from "@/types/enums/clinic-setup";
import type { PaymentMethodType } from "@/types/enums/payment";

export interface ClinicSetupMetadata {
  // Import
  import_status?: string;
  source_system?: string;
  source_system_display?: string;
  categories?: Record<string, string>;
  import_completed_at?: string;
  import_signed_off_at?: string;
  import_requested_at?: string;
  data_issues?: Array<{
    category: string;
    description: string;
    reported_at: string;
  }>;

  // Telephony
  phone_number?: string;
  phone_number_id?: string;
  sw_brand_payload?: Record<string, unknown>;
  sw_brand_id?: string;
  sw_brand_state?: string;
  sw_campaign_id?: string;
  sw_campaign_state?: string;
  sw_registration_submitted_at?: string;

  // Set by the Rainforest merchant_application webhook when a NEEDS_INFO /
  // DECLINED event on an already-complete tenant reopens the wizard at the
  // PAYMENTS step. Carries the affected MerchantAccount.id; cleared by the
  // recovery path (COMPLETED / IN_REVIEW) or when the user re-finishes
  // PAYMENTS. Presence signals "this tenant already completed the wizard
  // once — treat every step as read-only click-through, not a fresh
  // walk-through," so the reopen is a one-tap advance rather than a
  // re-collection of card / phone / merchant data they already provided.
  reopened_for_merchant?: string;
}

export interface ClinicSetupState {
  currentStep: ClinicSetupStep;
  flowType: ClinicSetupFlowType | null;
  metadataJson: ClinicSetupMetadata;
  skippedAt: string | null;
  stepOrder: ClinicSetupStep[];
  stepStatuses: Record<string, StepStatus>;
}

/** Mirrors decoda/schemas/clinic_setup.py. */
export type SetupCapability =
  | "PAYMENTS"
  | "POS_TERMINAL"
  | "TELEPHONY"
  | "EMAIL";

/** PENDING means a third party is working on it; ACTION_NEEDED means it's waiting */
export type SetupCapabilityState =
  | "READY"
  | "PENDING"
  | "ACTION_NEEDED"
  | "NOT_STARTED";

export interface CapabilityReadiness {
  capability: SetupCapability;
  state: SetupCapabilityState;
  /** Tenant-relative path where a clinic fixes this; null when there's nowhere */
  remediationPath: string | null;
  detail: string | null;
}

export interface SetupProgress {
  currentStep: ClinicSetupStep;
  stepOrder: ClinicSetupStep[];
  /** stepOrder minus the NOT_STARTED and COMPLETE sentinels, so a MIGRATING flow */
  totalSteps: number;
  stepsRemaining: number;
  isComplete: boolean;
  skipped: boolean;
  /** Negative once go-live has passed, null when no date is set. */
  daysToGoLive: number | null;
  /** Tenant predates the wizard (no ClinicSetup row); every capability READY. */
  isLegacyTenant: boolean;
  /** Someone has closed the "You're all set" panel. */
  completionDismissed: boolean;
  /** No go-live date, or more than a week past it — stop nagging and gating. */
  onboardingWindowClosed: boolean;
  /** Hold this admin in the wizard: inside the window and not finished yet. */
  setupLocked: boolean;
}

/** Separate fields because a clinic can have a card on file and still owe the */

export interface SetupReadiness {
  /** Keyed by capability rather than an overall verdict: some surfaces need more */
  capabilities: Partial<Record<SetupCapability, CapabilityReadiness>>;
  progress: SetupProgress;
}

export interface ProvisionPhoneResponse {
  phoneNumberId: string;
  number: string;
}

export interface MerchantOnboardingResponse {
  /**
   * Which processor this clinic is being underwritten on. The two hand back
   * different things and exactly one of the fields below is populated:
   * Rainforest a `sessionKey` for the component we mount, Stripe an
   * `onboardingUrl` we send the clinic away to.
   */
  processor: PaymentProcessor;
  sessionKey: string | null;
  /**
   * Stripe only. Valid for five minutes, so follow it now — never store it,
   * and mint a fresh one for each attempt.
   */
  onboardingUrl: string | null;
  merchantId: string;
  merchantApplicationId: string | null;
  applicationStatus: string | null;
  sandbox: boolean;
  /**
   * The location's Rainforest application, opened once Stripe declines the
   * clinic (or one it already had), so it can still take cards. Null
   * otherwise, or when it couldn't be opened — in which case
   * `fallbackUnavailableReason` says so in words fit for the clinic.
   */
  fallback?: MerchantOnboardingFallback | null;
  fallbackUnavailableReason?: string | null;
}

/** The Rainforest application, mounted with the component we already have. */
export interface MerchantOnboardingFallback {
  processor: PaymentProcessor;
  sessionKey: string | null;
  merchantId: string;
  merchantApplicationId: string | null;
  applicationStatus: string | null;
  sandbox: boolean;
}

export interface MerchantCandidateStatus {
  processor: PaymentProcessor;
  merchantId: string;
  applicationStatus: string | null;
  /** The application the location charges through, or is waiting on. */
  isActive: boolean;
  isPrimary: boolean;
}

export interface MerchantOnboardingStatusResponse {
  applicationStatus: string | null;
  /** Stripe only. */
  chargesEnabled?: boolean | null;
  payoutsEnabled?: boolean | null;
  outstandingRequirements?: string[] | null;
  activeProcessor?: PaymentProcessor | null;
  candidates?: MerchantCandidateStatus[] | null;
}

/**
 * Whether a Stripe merchant can take ACH (bank) payments. `achStatus` is
 * Stripe's word for the capability and null when it was never requested;
 * `reachable` is false when Stripe couldn't be asked.
 */
export interface StripeAchCapabilityResponse {
  achStatus: string | null;
  achEnabled: boolean;
  reachable: boolean;
}

/** What Connect.js needs to mount Stripe's embedded onboarding form. */
export interface StripeAccountSessionResponse {
  clientSecret: string;
  publishableKey: string | null;
  connectedAccountId: string;
  sandbox: boolean;
}

export enum BusinessStructure {
  SOLE_PROPRIETORSHIP = "SOLE_PROPRIETORSHIP",
  SINGLE_MEMBER_LLC = "SINGLE_MEMBER_LLC",
  MULTI_MEMBER_LLC = "MULTI_MEMBER_LLC",
  PARTNERSHIP = "PARTNERSHIP",
  LLP = "LLP",
  PRIVATE_CORPORATION = "PRIVATE_CORPORATION",
  PUBLIC_CORPORATION = "PUBLIC_CORPORATION",
  NON_PROFIT = "NON_PROFIT",
}

/**
 * The one business-details form, sent to every processor the clinic is being
 * underwritten on so it confirms rather than retypes. Nothing sensitive — tax
 * ids, bank details and owners' details go into each processor's own form.
 */
export interface MerchantBusinessDetails {
  legalName?: string | null;
  dbaName?: string | null;
  businessStructure?: BusinessStructure | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
}

export interface LocationMerchantStatus {
  locationId: string;
  locationName: string;
  merchantId: string | null;
  merchantName: string | null;
  applicationStatus: string | null;
  hasMerchant: boolean;
}

export interface MerchantOnboardingLocationsResponse {
  locations: LocationMerchantStatus[];
  anyMerchantSetUp: boolean;
}

export interface ImportReviewServiceItem {
  name: string;
  price: number;
  category: string | null;
  duration: number | null;
}

export interface ImportReviewProductItem {
  name: string;
  price: number;
  category: string | null;
}

export interface ImportReviewData {
  importStatus: string;
  sourceSystem: string | null;
  patientCount: number;
  serviceCount: number;
  services: ImportReviewServiceItem[];
  productCount: number;
  products: ImportReviewProductItem[];
  totalOutstandingBalance: number;
  categoryCounts: Record<string, number> | null;
}

export interface BusinessInfoPayload {
  companyName: string;
  dba: string;
  contactEmail: string;
  contactPhone: string;
  ein: string;
  legalEntityType: string;
  companyAddress: string;
  companyWebsite: string;
  privacyPolicyUrl: string;
}

export interface CampaignInfoPayload {
  description: string;
  sample1: string;
  sample2: string;
  messageFlow: string;
  optInMessage?: string;
  optOutMessage?: string;
  helpMessage?: string;
}

export interface RegistrationStatus {
  swBrandId: string | null;
  swBrandState: string | null;
  swCampaignId: string | null;
  swCampaignState: string | null;
  swRegistrationSubmittedAt: string | null;
  phoneNumber: string | null;
  phoneNumberId: string | null;
  /** A previous submission, for prefilling a resubmission. */
  swBrandPayload: Partial<BusinessInfoPayload> | null;
  // True when the clinic chose to port an existing number instead of
  // picking a new SignalWire one during the telephony wizard step.
  phonePortRequested: boolean;
}

export interface BusinessInfoPrefill {
  companyName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  companyAddress: string | null;
}

export interface BillingPaymentMethodConfigResponse {
  sessionKey: string;
  paymentMethodConfigId: string;
  sandbox: boolean;
  setupFeeCents: number;
  // True when the tenant already has a default payment method on file in the
  // billing schema. When set, sessionKey / paymentMethodConfigId are empty
  // strings and the BillingStep should render its "already configured"
  // success screen with a Continue button instead of the add-card form.
  alreadyConfigured?: boolean;
}

export interface SubmitBillingCardResponse {
  success: boolean;
  chargeAmount: number;
  message: string;
}

export interface PosTerminalOption {
  id: string;
  name: string;
  priceCents: number;
  features: string[];
}

export interface ShippableLocation {
  id: string;
  name: string;
  address: string;
}

export interface PosTerminalCatalogResponse {
  terminals: PosTerminalOption[];
  /**
   * The processor the terminals are for. A reader only takes payments for the
   * processor it was bought from, so this follows the ship-to location's
   * merchant. STRIPE means no terminals: the clinic buys in Stripe's shop.
   */
  processor?: PaymentProcessor;
  /** Set when nothing can be ordered right now; written to be shown as-is. */
  orderingBlockedReason?: string | null;
  maxQuantity: number;
  /** Default payment method in the billing tenant, or null if none is saved. */
  paymentMethodType: PaymentMethodType | null;
  paymentMethodLabel: string | null;
  /** Where the terminal will be shipped, or null if no address is on file. */
  shippingAddress: string | null;
  /** Locations a terminal can ship to. The wizard ignores this; the in-app
   *  ordering screen offers a choice when there is more than one. */
  locations: ShippableLocation[];
  /** An order already placed by this clinic, if any. */
  existingOrder: PosTerminalOrderResponse | null;
}

/** Submission state of the money side. Settlement is `chargeStatus`. */
export type PosOrderPayment =
  | "PENDING"
  | "SUBMITTED"
  | "FAILED"
  | "NOT_CHARGED";

/** Whether the vendor was actually told to ship. */
export type PosOrderFulfillment =
  | "PENDING"
  | "SENDING"
  | "SENT"
  | "FAILED"
  | "SUPPRESSED";

export interface PosTerminalOrderResponse {
  terminalId: string;
  terminalName: string;
  quantity: number;
  unitPriceCents: number;
  totalCents: number;
  chargeId: string | null;
  paymentStatus: PosOrderPayment;
  fulfillmentStatus: PosOrderFulfillment;
  /** Charge status in billing, kept current by the Rainforest webhooks. */
  chargeStatus: string | null;
}
